import React, { FC, useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  ModelType,
  Course,
  Option,
  RandomStaff,
  SettingBookingTargets,
} from '../../../../../interfaces/merchants/resources/books';

import {
  inputBookingTargets,
  RestartReservationError,
} from '../../../../../redux/actions/merchants/resources/books/inputs';

import LabeledContainer from '../../../../../molecules/common/LabeledContainer';
import InputBookingTargetCourseSelector from '../InputBookingTargetCourseSelector';
import InputBookingTargetOptionSelector from '../InputBookingTargetOptionSelector';
import Preview from '../../../../../molecules/merchant/resources/book/InputBookingTargetPreview';
import { scrollToTop } from '../../../../../hooks/useScrollToTop';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';
import { useAppDispatch } from '../../../../../redux';
import { RSButton } from 'src/reservation-page/RSButton';

interface ElementProps {
  modelType: ModelType;
  settings: SettingBookingTargets;
  onSubmit: () => void;
  onRestart: () => void;
  renew?: boolean;
  postable: boolean;
  withExpressFlow: boolean;
  resourcePublicId: string;
}

interface State {
  course?: Course;
  options?: Option[];
}

type Props = ElementProps;

const InputBookingTarget: FC<Props> = (props) => {
  const dispatch = useAppDispatch();

  const { settings, postable, renew, resourcePublicId } = props;
  const intl = useIntl();

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));
  const { inputs } = useSelector();

  const [course, setCourse] = useState<Course | undefined>(
    renew ? undefined : inputs.course,
  );
  const [options, setOptions] = useState<Option[] | undefined>(
    renew ? [] : inputs.options || [],
  );

  const _handleSubmit = useCallback(
    async ({ course, options }: State) => {
      const { modelType, onSubmit, onRestart, withExpressFlow } = props;
      const { merchantId, reservationId } = inputs;

      try {
        const randomStaff: RandomStaff = { id: '', query_id: '' };
        await dispatch(
          inputBookingTargets(
            modelType,
            merchantId!,
            resourcePublicId,
            { course: course!, options: options, staff: randomStaff },
            withExpressFlow,
            reservationId,
          ),
        );
        onSubmit();
      } catch (e) {
        if (e instanceof RestartReservationError) {
          onRestart();
        }
      }
    },
    [dispatch, props, inputs, resourcePublicId],
  );

  const handleClickCourse = useCallback(
    (course: Course) => {
      if (course.options.length === 0) {
        // 選択されたコースにオプションが存在しなければオプション選択はskip
        _handleSubmit({ course, options: [] });
        return;
      }
      setCourse(course);
    },
    [_handleSubmit],
  );

  const handleClickOption = useCallback((options: Option[]) => {
    setOptions(options);
  }, []);

  const handleSubmit = useCallback(async () => {
    await _handleSubmit({ course, options });
  }, [_handleSubmit, course, options]);

  const handleChangeTarget = useCallback(
    (targets: { course: boolean; options: boolean }) => {
      const { course, options } = targets;
      if (!course && options) {
        throw new Error(
          'Selected Options should not be able to change in this component.',
        );
      }
      if (course) {
        setCourse(undefined);
        setOptions([]);
      }
    },
    [],
  );

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    const selectedCourse = props.settings.selectedCourse;
    const reservationId = inputs.reservationId;

    // exclude target reselect.
    if (!reservationId && selectedCourse) {
      handleClickCourse(selectedCourse);
    }
  }, [props.settings.selectedCourse, inputs.reservationId, handleClickCourse]);

  return (
    <>
      {!course || course.options.length === 0 ? (
        <InputBookingTargetCourseSelector
          courses={settings.courses}
          postable={postable}
          onClick={handleClickCourse}
        />
      ) : (
        <div>
          <Preview
            course={course}
            options={[]} // 選択中のoptionは表示しない
            onChangeTarget={handleChangeTarget}
          />
          <LabeledContainer
            label={intl.formatMessage({
              id: 'books.formsLabel.addOptions',
            })}
            paddingTop={true}
          >
            <InputBookingTargetOptionSelector
              options={course.options}
              checkedOptions={options || []}
              onClick={handleClickOption}
            />
          </LabeledContainer>
          <div className="px-[18px] py-5">
            <RSButton
              variant="basic"
              isBlock
              isLoading={!postable}
              disabled={!postable}
              type="button"
              value={intl.formatMessage({
                id: 'books.submitLabel.selectDateTime',
              })}
              onClick={handleSubmit}
            >
              {intl.formatMessage({
                id: 'books.submitLabel.selectDateTime',
              })}
            </RSButton>
          </div>
        </div>
      )}
    </>
  );
};

export default InputBookingTarget;
